import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users, Download, ExternalLink } from "lucide-react";
import { EventRow, Speaker, SPECIALTIES, CATERING_OPTIONS, fmtDate, fmtTime, fmtPrice } from "@/lib/events";

interface Reg {
  id: string; event_id: string; first_name: string; last_name: string; email: string;
  phone: string | null; persons: number; note: string | null; status: string; created_at: string;
}

const slugify = (s: string) =>
  s.toLowerCase().replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80);

const toLocalInput = (iso: string) => {
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};

const empty = {
  id: "", slug: "", title: "", title_en: "", description: "", description_en: "", cover_image_url: "",
  location: "", starts_at: "", duration_minutes: 90, price_eur: 0, max_participants: 30, published: false, speakers: [] as Speaker[],
  registration_open: true, date_note: "", date_note_en: "", catering: [] as string[], qa: false,
};

const AdminEvents = () => {
  const [events, setEvents] = useState<EventRow[]>([]);
  const [regs, setRegs] = useState<Reg[]>([]);
  const [edit, setEdit] = useState<typeof empty | null>(null);
  const [openRegs, setOpenRegs] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    const [{ data: e }, { data: r }] = await Promise.all([
      supabase.from("events").select("*").order("starts_at", { ascending: false, nullsFirst: true }),
      supabase.from("event_registrations").select("*").order("created_at"),
    ]);
    setEvents((e as unknown as EventRow[]) ?? []);
    setRegs((r as Reg[]) ?? []);
  };
  useEffect(() => { load(); }, []);

  const taken = (id: string) => regs.filter((r) => r.event_id === id && r.status === "registered").reduce((a, r) => a + r.persons, 0);

  const save = async () => {
    if (!edit) return;
    if (!edit.title.trim()) return toast.error("Titel ist Pflicht.");
    if (edit.registration_open && (!edit.starts_at || !edit.location.trim())) return toast.error("Für eine Anmeldung sind Datum/Uhrzeit und Ort Pflicht – oder „Anmeldung offen“ ausschalten.");
    const payload = {
      slug: edit.slug.trim() || slugify(edit.title),
      title: edit.title.trim(), title_en: edit.title_en.trim() || null,
      description: edit.description, description_en: edit.description_en.trim() || null,
      cover_image_url: edit.cover_image_url.trim() || null, location: edit.location.trim(),
      starts_at: edit.starts_at ? new Date(edit.starts_at).toISOString() : null,
      registration_open: edit.registration_open,
      date_note: edit.date_note.trim() || null, date_note_en: edit.date_note_en.trim() || null,
      duration_minutes: Number(edit.duration_minutes), price_eur: Number(edit.price_eur),
      max_participants: Number(edit.max_participants), published: edit.published,
      speakers: edit.speakers.filter((sp) => sp.name.trim()) as unknown as never,
      catering: edit.catering,
      qa: edit.qa,
    };
    const { error } = edit.id
      ? await supabase.from("events").update(payload).eq("id", edit.id)
      : await supabase.from("events").insert(payload);
    if (error) return toast.error(error.message);
    toast.success("Gespeichert");
    setEdit(null);
    load();
  };

  const remove = async (id: string) => {
    if (!confirm("Veranstaltung inkl. aller Anmeldungen löschen?")) return;
    await supabase.from("events").delete().eq("id", id);
    load();
  };

  const upload = async (file: File) => {
    setUploading(true);
    const path = `events/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.]/g, "_")}`;
    const { error } = await supabase.storage.from("blog-images").upload(path, file);
    setUploading(false);
    if (error) return toast.error(error.message);
    const url = supabase.storage.from("blog-images").getPublicUrl(path).data.publicUrl;
    setEdit((e) => e && { ...e, cover_image_url: url });
  };

  const setSp = (i: number, patch: Partial<Speaker>) =>
    setEdit((e) => e && { ...e, speakers: e.speakers.map((sp, j) => (j === i ? { ...sp, ...patch } : sp)) });

  const uploadSp = async (i: number, file: File) => {
    const path = `speakers/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.]/g, "_")}`;
    const { error } = await supabase.storage.from("blog-images").upload(path, file);
    if (error) return toast.error(error.message);
    setSp(i, { photo_url: supabase.storage.from("blog-images").getPublicUrl(path).data.publicUrl });
  };

  const setStatus = async (r: Reg, status: string) => {
    await supabase.from("event_registrations").update({ status }).eq("id", r.id);
    load();
  };

  const csv = (ev: EventRow) => {
    const rows = regs.filter((r) => r.event_id === ev.id);
    const q = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [["Vorname", "Nachname", "E-Mail", "Telefon", "Personen", "Anmerkung", "Status", "Angemeldet am"].join(";"),
      ...rows.map((r) => [r.first_name, r.last_name, r.email, r.phone, r.persons, r.note, r.status, new Date(r.created_at).toLocaleString("de-AT")].map(q).join(";"))];
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `anmeldungen-${ev.slug}.csv`;
    a.click();
  };

  const f = (k: keyof typeof empty) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setEdit((s) => s && { ...s, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="border-b bg-background">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between max-w-5xl">
          <h1 className="text-xl font-semibold">Veranstaltungen</h1>
          <Button variant="outline" asChild><Link to="/admin/blog">← Zurück zum Admin</Link></Button>
        </div>
      </header>
      <main className="container mx-auto px-4 py-8 max-w-5xl space-y-4">
        {!edit && <Button onClick={() => setEdit({ ...empty })}><Plus className="w-4 h-4 mr-2" />Neue Veranstaltung</Button>}

        {edit && (
          <Card className="p-6 space-y-3">
            <h2 className="font-semibold text-lg">{edit.id ? "Veranstaltung bearbeiten" : "Neue Veranstaltung"}</h2>
            <Input placeholder="Titel (Thema) *" value={edit.title} onChange={f("title")} />
            <Input placeholder="URL-Kürzel (leer = automatisch)" value={edit.slug} onChange={f("slug")} />
            <Textarea rows={8} placeholder="Beschreibung – worum geht es? (Absätze mit Leerzeile trennen)" value={edit.description} onChange={f("description")} />
            <div className="grid md:grid-cols-2 gap-3">
              <label className="text-sm">Datum & Uhrzeit (leer = noch offen)<Input type="datetime-local" value={edit.starts_at} onChange={f("starts_at")} /></label>
              <label className="text-sm">Ort<Input placeholder="z. B. Praxis Innere Balance, Wien" value={edit.location} onChange={f("location")} /></label>
              <label className="text-sm">Hinweis statt Datum (z. B. „Frühjahr 2027“)<Input value={edit.date_note} onChange={f("date_note")} /></label>
              <label className="text-sm">Hinweis statt Datum (EN, optional)<Input value={edit.date_note_en} onChange={f("date_note_en")} /></label>
              <label className="text-sm">Dauer (Minuten)<Input type="number" min={15} value={edit.duration_minutes} onChange={f("duration_minutes")} /></label>
              <label className="text-sm">Preis pro Person (€, 0 = kostenlos)<Input type="number" min={0} step="0.01" value={edit.price_eur} onChange={f("price_eur")} /></label>
              <label className="text-sm">Max. Teilnehmende<Input type="number" min={1} value={edit.max_participants} onChange={f("max_participants")} /></label>
              <label className="text-sm">Titelbild
                <Input type="file" accept="image/*" disabled={uploading} onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
              </label>
            </div>
            {edit.cover_image_url && <img src={edit.cover_image_url} alt="" className="h-32 rounded-md object-cover" />}
            <div className="rounded-md border p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">Gastauftritte / Vortragende</h3>
                <Button size="sm" variant="outline" onClick={() => setEdit({ ...edit, speakers: [...edit.speakers, { name: "", title: "", specialty: "" }] })}>
                  <Plus className="w-4 h-4 mr-1" />Gast hinzufügen
                </Button>
              </div>
              {edit.speakers.length === 0 && <p className="text-sm text-muted-foreground">Kein Gast – nur Sie tragen vor.</p>}
              <datalist id="specialties">{SPECIALTIES.map((s) => <option key={s} value={s} />)}</datalist>
              {edit.speakers.map((sp, i) => (
                <div key={i} className="rounded-md bg-muted/40 p-3 space-y-2">
                  <div className="grid md:grid-cols-3 gap-2">
                    <Input placeholder="Titel (z. B. Dr. med.)" value={sp.title} onChange={(e) => setSp(i, { title: e.target.value })} />
                    <Input placeholder="Name *" value={sp.name} onChange={(e) => setSp(i, { name: e.target.value })} />
                    <Input list="specialties" placeholder="Fachrichtung (z. B. Urologie)" value={sp.specialty} onChange={(e) => setSp(i, { specialty: e.target.value })} />
                  </div>
                  <Textarea rows={2} placeholder="Kurzbeschreibung (optional)" value={sp.bio ?? ""} onChange={(e) => setSp(i, { bio: e.target.value })} />
                  <div className="grid md:grid-cols-2 gap-2">
                    <Input placeholder="Fachrichtung Englisch (optional)" value={sp.specialty_en ?? ""} onChange={(e) => setSp(i, { specialty_en: e.target.value })} />
                    <Input placeholder="Kurzbeschreibung Englisch (optional)" value={sp.bio_en ?? ""} onChange={(e) => setSp(i, { bio_en: e.target.value })} />
                  </div>
                  <div className="flex items-center gap-3">
                    {sp.photo_url && <img src={sp.photo_url} alt="" className="w-12 h-12 rounded-full object-cover" />}
                    <Input type="file" accept="image/*" className="max-w-xs" onChange={(e) => e.target.files?.[0] && uploadSp(i, e.target.files[0])} />
                    <Button size="sm" variant="ghost" onClick={() => setEdit({ ...edit, speakers: edit.speakers.filter((_, j) => j !== i) })}>
                      <Trash2 className="w-4 h-4 mr-1" />Entfernen
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="rounded-md border p-4 space-y-2">
              <h3 className="font-medium">Verpflegung</h3>
              <p className="text-sm text-muted-foreground">Anhaken, was es bei der Veranstaltung gibt – wird öffentlich angezeigt.</p>
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                {CATERING_OPTIONS.map((c) => (
                  <label key={c.key} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={edit.catering.includes(c.key)}
                      onChange={(e) =>
                        setEdit((s) => s && { ...s, catering: e.target.checked ? [...s.catering, c.key] : s.catering.filter((k) => k !== c.key) })
                      }
                    />
                    {c.de}
                  </label>
                ))}
              </div>
            </div>
            <div className="rounded-md border p-4 space-y-2">
              <label className="flex items-start gap-3 text-sm">
                <Switch checked={edit.qa} onCheckedChange={(v) => setEdit({ ...edit, qa: v })} className="mt-0.5" />
                <span>
                  <span className="font-medium">Persönliche Q&amp;A / Fragerunde</span>
                  <span className="block text-muted-foreground">
                    Anhaken, wenn die Teilnehmenden selbst Fragen stellen können – wird öffentlich als „Mit Fragerunde“ angezeigt.
                  </span>
                </span>
              </label>
            </div>
            <details className="text-sm">
              <summary className="cursor-pointer text-muted-foreground">Englische Fassung (optional)</summary>
              <div className="space-y-2 mt-2">
                <Input placeholder="Title (English)" value={edit.title_en} onChange={f("title_en")} />
                <Textarea rows={6} placeholder="Description (English)" value={edit.description_en} onChange={f("description_en")} />
              </div>
            </details>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={edit.published} onCheckedChange={(v) => setEdit({ ...edit, published: v })} /> Veröffentlicht
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={edit.registration_open} onCheckedChange={(v) => setEdit({ ...edit, registration_open: v })} /> Anmeldung offen
              <span className="text-muted-foreground">(aus = nur Ankündigung „Demnächst“, ohne Anmeldung)</span>
            </label>
            <div className="flex gap-2">
              <Button onClick={save}>Speichern</Button>
              <Button variant="outline" onClick={() => setEdit(null)}>Abbrechen</Button>
            </div>
          </Card>
        )}

        {events.map((ev) => {
          const list = regs.filter((r) => r.event_id === ev.id);
          return (
            <Card key={ev.id} className="p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold">{ev.title}</h3>
                    <Badge variant={ev.published ? "default" : "secondary"}>{ev.published ? "Online" : "Entwurf"}</Badge>
                    {ev.starts_at && new Date(ev.starts_at) < new Date() && <Badge variant="outline">Vergangen</Badge>}
                    {(!ev.registration_open || !ev.starts_at) && <Badge variant="outline">Nur Ankündigung</Badge>}
                  </div>
                  {(ev.speakers ?? []).length > 0 && (
                    <p className="text-sm">Gäste: {(ev.speakers ?? []).map((sp) => `${sp.title} ${sp.name} (${sp.specialty})`.trim()).join(", ")}</p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    {ev.starts_at ? `${fmtDate(ev.starts_at, "de")}, ${fmtTime(ev.starts_at, "de")}` : (ev.date_note || "Termin offen")} · {ev.location || "Ort offen"} · {fmtPrice(ev.price_eur, "de")} · {taken(ev.id)}/{ev.max_participants} Plätze belegt
                  </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => setOpenRegs(openRegs === ev.id ? null : ev.id)}><Users className="w-4 h-4 mr-1" />{list.length}</Button>
                  <Button size="sm" variant="outline" onClick={() => csv(ev)}><Download className="w-4 h-4" /></Button>
                  {ev.published && <Button size="sm" variant="outline" asChild><a href={`/veranstaltungen/${ev.slug}`} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4" /></a></Button>}
                  <Button size="sm" variant="outline" onClick={() => setEdit({
                    id: ev.id, slug: ev.slug, title: ev.title, title_en: ev.title_en ?? "", description: ev.description,
                    description_en: ev.description_en ?? "", cover_image_url: ev.cover_image_url ?? "", location: ev.location,
                    starts_at: ev.starts_at ? toLocalInput(ev.starts_at) : "",
                    registration_open: ev.registration_open, date_note: ev.date_note ?? "", date_note_en: ev.date_note_en ?? "", duration_minutes: ev.duration_minutes, price_eur: Number(ev.price_eur),
                    max_participants: ev.max_participants, published: ev.published, speakers: ev.speakers ?? [],
                    catering: ev.catering ?? [], qa: ev.qa ?? false,
                  })}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => remove(ev.id)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
              {openRegs === ev.id && (
                <div className="mt-4 border-t pt-3 text-sm space-y-2">
                  {list.length === 0 ? <p className="text-muted-foreground">Noch keine Anmeldungen.</p> : list.map((r) => (
                    <div key={r.id} className="flex flex-wrap justify-between gap-2 py-1 border-b last:border-0">
                      <div>
                        <span className={r.status === "cancelled" ? "line-through text-muted-foreground" : "font-medium"}>{r.first_name} {r.last_name}</span>
                        {" · "}{r.persons} Pers. · <a href={`mailto:${r.email}`} className="text-primary">{r.email}</a>{r.phone ? ` · ${r.phone}` : ""}
                        {r.note && <p className="text-muted-foreground text-xs">{r.note}</p>}
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => setStatus(r, r.status === "cancelled" ? "registered" : "cancelled")}>
                        {r.status === "cancelled" ? "Reaktivieren" : "Stornieren"}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </main>
    </div>
  );
};

export default AdminEvents;
